import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { initiateThoughtlyCall } from '../../lib/thoughtly';
import { FiMic, FiPhone, FiShield, FiX, FiLoader, FiUser } from 'react-icons/fi';

const QuickAICallModal = ({ contact, agent, onClose, onCallInitiated }) => {
  const { settings } = useSettings();
  const [consentGiven, setConsentGiven] = useState(true);
  const [isInitiating, setIsInitiating] = useState(false);

  const isCallDisabled = !agent || !contact?.phone || !consentGiven || isInitiating;

  const handleInitiateCall = async () => {
    if (isCallDisabled) {
      return;
    }

    if (!settings.thoughtlyApiKey || !settings.thoughtlyTeamId) {
      toast.error('Thoughtly API credentials not configured');
      return;
    }

    setIsInitiating(true);

    try {
      const response = await initiateThoughtlyCall({
        contact,
        agent,
        customVariables: {},
        overrides: {
          apiKey: settings.thoughtlyApiKey,
          teamId: settings.thoughtlyTeamId
        }
      });

      const responseId =
        response?.data?.response_id ||
        response?.data?.id ||
        response?.response_id ||
        response?.responseId ||
        response?.call?.response_id ||
        response?.call?.id;

      const callId =
        response?.call_id ||
        response?.callId ||
        response?.data?.call_id ||
        response?.data?.callId ||
        response?.id ||
        Date.now().toString();

      const callData = {
        id: callId,
        agentId: agent.id,
        agentName: agent.name,
        contactId: contact.id,
        phoneNumber: contact.phone,
        customVariables: {},
        initiatedAt: new Date().toISOString(),
        provider: 'thoughtly',
        providerResponse: response,
        responseId,
        callId
      };

      onCallInitiated(callData);
      toast.success('Quick AI call initiated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to initiate quick AI call:', error);
      toast.error(error.message || 'Failed to initiate AI call');
    } finally {
      setIsInitiating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMic} className="text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Quick AI Call</h2>
              <p className="text-sm text-gray-600">Confirm and start an AI call instantly</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isInitiating}
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiUser} className="text-purple-600" />
              <div>
                <p className="text-sm text-gray-500">Contact</p>
                <p className="font-medium text-gray-900">
                  {contact?.firstName} {contact?.lastName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiPhone} className="text-purple-600" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{contact?.phone || 'No phone number available'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiMic} className="text-purple-600" />
              <div>
                <p className="text-sm text-gray-500">AI Agent</p>
                <p className="font-medium text-gray-900">
                  {agent ? agent.name : 'No recommended agent available'}
                </p>
                {agent?.description && (
                  <p className="text-xs text-gray-500 mt-1">{agent.description}</p>
                )}
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-purple-300 transition-colors">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded"
              checked={consentGiven}
              onChange={(event) => setConsentGiven(event.target.checked)}
              disabled={isInitiating}
            />
            <div>
              <p className="text-sm font-medium text-gray-900">I confirm I have consent to contact this person.</p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <SafeIcon icon={FiShield} className="text-purple-600" />
                Compliance consent is required for AI calls.
              </p>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
            disabled={isInitiating}
          >
            Cancel
          </button>
          <button
            onClick={handleInitiateCall}
            disabled={isCallDisabled}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors ${
              isCallDisabled
                ? 'bg-purple-300 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {isInitiating ? (
              <>
                <SafeIcon icon={FiLoader} className="animate-spin" />
                Calling...
              </>
            ) : (
              <>
                <SafeIcon icon={FiPhone} />
                Call Now
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default QuickAICallModal;
